<div class="client-milestone-tab" [attr.data-testid]="'client-milestone-tab'">
  <!-- Header with Title and Actions -->
  <div class="flex align-items-center justify-content-between p-3">
    <div class="text-base md:text-lg font-bold" [attr.data-testid]="'milestone-title'">
      {{ 'CLIENT_MILESTONE.TITLE' | translate }}
    </div>
    <div class="flex gap-2">
      <button
        pButton
        pRipple
        type="button"
        [label]="'CLIENT_MILESTONE.ACTIONS.RECOMPUTE' | translate"
        icon="pi pi-refresh"
        class="p-button-outlined text-sm"
        (click)="handleRecompute()"
        [loading]="recomputeLoading()"
        [disabled]="loading() || recomputeLoading()"
        [attr.aria-label]="'recompute-milestones-button'"
        [attr.data-testid]="'recompute-milestones-button'"
      ></button>
      <button
        pButton
        pRipple
        type="button"
        [label]="'CLIENT_MILESTONE.ACTIONS.OVERRIDE_AWARD' | translate"
        icon="pi pi-plus"
        class="text-sm"
        (click)="handleOverrideAward()"
        [disabled]="loading() || recomputeLoading()"
        [attr.aria-label]="'override-award-button'"
        [attr.data-testid]="'override-award-button'"
      ></button>
    </div>
  </div>

  <!-- Summary Cards -->
  <div class="grid mb-1 p-3" [attr.data-testid]="'milestone-summary'">
    <!-- Achieved -->
    <div class="col-12 md:col-6 lg:col-3">
      <div class="surface-0 shadow-1 border-round p-3 border-1 border-green-500">
        <div class="flex align-items-center justify-content-between mb-2">
          <div>
            <span class="block text-500 font-medium text-xs uppercase">
              {{ 'CLIENT_MILESTONE.SUMMARY.ACHIEVED' | translate }}
            </span>
            <div class="text-green-500 font-bold text-2xl">
              {{ summary().achieved }}
            </div>
          </div>
          <div class="flex align-items-center justify-content-center bg-green-100 border-round" style="width: 3rem; height: 3rem;">
            <i class="pi pi-check-circle text-green-500 text-2xl"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- In Progress -->
    <div class="col-12 md:col-6 lg:col-3">
      <div class="surface-0 shadow-1 border-round p-3 border-1 border-orange-500">
        <div class="flex align-items-center justify-content-between mb-2">
          <div>
            <span class="block text-500 font-medium text-xs uppercase">
              {{ 'CLIENT_MILESTONE.SUMMARY.IN_PROGRESS' | translate }}
            </span>
            <div class="text-orange-500 font-bold text-2xl">
              {{ summary().inProgress }}
            </div>
          </div>
          <div class="flex align-items-center justify-content-center bg-orange-100 border-round" style="width: 3rem; height: 3rem;">
            <i class="pi pi-chart-line text-orange-500 text-2xl"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Not Started -->
    <div class="col-12 md:col-6 lg:col-3">
      <div class="surface-0 shadow-1 border-round p-3 border-1 border-300">
        <div class="flex align-items-center justify-content-between mb-2">
          <div>
            <span class="block text-500 font-medium text-xs uppercase">
              {{ 'CLIENT_MILESTONE.SUMMARY.NOT_STARTED' | translate }}
            </span>
            <div class="text-600 font-bold text-2xl">
              {{ summary().notStarted }}
            </div>
          </div>
          <div class="flex align-items-center justify-content-center bg-gray-100 border-round" style="width: 3rem; height: 3rem;">
            <i class="pi pi-lock text-600 text-2xl"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Total -->
    <div class="col-12 md:col-6 lg:col-3">
      <div class="surface-0 shadow-1 border-round p-3 border-1 border-primary">
        <div class="flex align-items-center justify-content-between mb-2">
          <div>
            <span class="block text-500 font-medium text-xs uppercase">
              {{ 'CLIENT_MILESTONE.SUMMARY.TOTAL' | translate }}
            </span>
            <div class="text-primary font-bold text-2xl">
              {{ summary().total }}
            </div>
          </div>
          <div class="flex align-items-center justify-content-center bg-blue-100 border-round" style="width: 3rem; height: 3rem;">
            <i class="pi pi-trophy text-primary text-2xl"></i>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Milestone Cards Grid -->
  @if (loading()) {
    <div class="flex justify-content-center align-items-center p-5" [attr.data-testid]="'milestone-loading'">
      <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
    </div>
  } @else if (milestoneCards().length === 0) {
    <div class="surface-0 shadow-1 border-round p-5 text-center" [attr.data-testid]="'no-milestones'">
      <i class="pi pi-trophy text-4xl text-400 mb-3"></i>
      <p class="text-600 mb-0 font-medium">
        {{ 'CLIENT_MILESTONE.NO_MILESTONES' | translate }}
      </p>
    </div>
  } @else {
    <div class="grid p-3">
      @for (card of milestoneCards(); track card.milestoneConfigId) {
        <div class="col-12 md:col-6 lg:col-4 xl:col-3 mb-3" [attr.data-testid]="'milestone-card-' + card.milestoneConfigId">
          <div class="surface-0 shadow-2 border-round-lg p-4 h-full flex flex-column relative"
               [class.border-2]="card.status === 'achieved'"
               [class.border-green-500]="card.status === 'achieved'"
               [class.border-1]="card.status !== 'achieved'"
               [class.border-300]="card.status !== 'achieved'">
            
            <!-- Top Section: Icon Only -->
            <div class="flex align-items-start justify-content-between mb-3">
              <!-- Hexagon Icon -->
              <div class="flex align-items-center justify-content-center border-round-lg p-3"
                   [class.bg-green-50]="card.status === 'achieved'"
                   [class.bg-orange-50]="card.status === 'in-progress'"
                   [class.bg-gray-50]="card.status === 'not-started'"
                   style="width: 4rem; height: 4rem;">
                @if (card.status === 'achieved') {
                  <i class="pi pi-check-circle text-green-500 text-3xl"></i>
                } @else if (card.status === 'in-progress') {
                  <i class="pi pi-chart-line text-orange-500 text-3xl"></i>
                } @else {
                  <i class="pi pi-lock text-400 text-3xl"></i>
                }
              </div>
            </div>

            <!-- Three-dot Menu for Achieved Milestones -->
            @if (card.status === 'achieved') {
              <div class="absolute top-0 right-0 m-2">
                <button
                  #menuButton
                  pButton
                  type="button"
                  icon="pi pi-ellipsis-v"
                  class="p-button-text p-button-rounded p-button-plain"
                  [style]="{ width: '2rem', height: '2rem' }"
                  (click)="toggleMilestoneOverlay(card.milestoneConfigId)"
                  [attr.aria-label]="'milestone-menu-' + card.milestoneConfigId"
                  [attr.data-testid]="'milestone-menu-' + card.milestoneConfigId"
                ></button>
                
                <p-overlay
                  [visible]="isOverlayVisible(card.milestoneConfigId)"
                  [target]="menuButton"
                  [appendTo]="'body'"
                  (onHide)="closeMilestoneOverlay()"
                >
                  <div class="surface-overlay shadow-2 border-round" style="min-width: 12rem;">
                    <ul class="list-none p-0 m-0">
                      <li>
                        <button
                          type="button"
                          class="w-full text-left p-3  cursor-pointer border-noround flex align-items-center gap-2"
                          style="border: none; background: transparent;"
                          (click)="onRevokeClick(card)"
                          [attr.aria-label]="'revoke-milestone-action'"
                          [attr.data-testid]="'revoke-milestone-action'"
                        >
                          <i class="pi pi-times-circle text-red-500"></i>
                          <span class="text-color">{{ 'CLIENT_MILESTONE.ACTIONS.REVOKE' | translate }}</span>
                        </button>
                      </li>
                      <!-- Add more menu items here in the future -->
                    </ul>
                  </div>
                </p-overlay>
              </div>
            }

            <!-- Milestone Name -->
            <div class="font-bold text-lg mb-2 text-900" [attr.data-testid]="'milestone-name'">
              {{ card.name }}
            </div>

            <!-- Achieved Date (for achieved milestones) -->
            @if (card.status === 'achieved' && card.achievedDate) {
              <div class="text-sm text-600 mb-2">
                {{ card.achievedDate | date: 'd MMM yyyy' }}
              </div>
            }

            <!-- Progress Text (for in-progress milestones) -->
            @if (card.status === 'in-progress') {
              <div class="text-sm text-600 mb-2">
                {{ card.currentValue }} / {{ card.targetValue }} {{ card.unit }}
              </div>
            }

            <!-- Description (hide for achieved to save space) -->
            @if (card.status !== 'achieved') {
              <div class="text-xs text-500 mb-3" [attr.data-testid]="'milestone-description'">
                {{ card.description }}
              </div>
            }

            <!-- Progress Bar (for in-progress milestones) -->
            @if (card.status === 'in-progress') {
              <div class="mb-3">
                <p-progressbar
                  [value]="card.progressPercentage"
                  [showValue]="false"
                  [styleClass]="'milestone-progress-in-progress'"
                  style="height: 0.5rem;"
                  [attr.data-testid]="'milestone-progress-bar'"
                ></p-progressbar>
              </div>
            }

            <!-- Reward Type Footer -->
            <div class="mt-auto pt-3 border-top-1 border-300">
              <div class="flex align-items-center text-sm text-600 mb-2">
                <i [class]="card.rewardIcon + ' mr-2'"></i>
                <span>{{ getRewardLabel(card.rewardType) }}</span>
              </div>

              <!-- Status Labels at Bottom -->
              <div class="flex align-items-center gap-2">
                @if (card.status === 'achieved') {
                  <span class="px-2 py-1 border-round text-xs font-bold bg-green-100 text-green-700 flex align-items-center gap-1">
                    <i class="pi pi-check"></i>
                    <span>{{ 'CLIENT_MILESTONE.STATUS.ACHIEVED' | translate }}</span>
                  </span>
                } @else if (card.status === 'in-progress') {
                  <span class="text-xs font-bold text-orange-600">
                    {{ card.progressPercentage }}%
                  </span>
                } @else if (card.status === 'not-started') {
                  <span class="px-2 py-1 border-round text-xs font-medium bg-gray-100 text-500 flex align-items-center gap-1">
                    <i class="pi pi-lock"></i>
                    <span>{{ 'CLIENT_MILESTONE.STATUS.NOT_STARTED' | translate }}</span>
                  </span>
                }
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }
</div>

<!-- Override & Award Badge Dialog -->
<p-dialog
  [visible]="showOverrideDialog()"
  (onHide)="closeOverrideDialog()"
  [modal]="true"
  [resizable]="false"
  [draggable]="false"
  [dismissableMask]="true"
  [closeOnEscape]="true"
  [style]="{ width: '500px' }"
  appendTo="body"
  [breakpoints]="{ '767px': '95vw' }"
  [header]="'CLIENT_MILESTONE.OVERRIDE_AWARD.TITLE' | translate"
  [attr.aria-label]="'override-award-dialog'"
  [attr.data-testid]="'override-award-dialog'"
>
  <div class="p-fluid" [formGroup]="awardForm">
    <!-- Milestone Selection -->
    <div class="field mb-4">
      <label for="milestone-select" class="block text-900 font-medium mb-2">
        {{ 'CLIENT_MILESTONE.OVERRIDE_AWARD.SELECT_MILESTONE' | translate }}
      </label>
      <p-select
        id="milestone-select"
        formControlName="milestoneConfigId"
        [options]="availableMilestones()"
        optionLabel="name"
        optionValue="id"
        [placeholder]="'CLIENT_MILESTONE.OVERRIDE_AWARD.MILESTONE_PLACEHOLDER' | translate"
        (onChange)="onMilestoneSelected($event)"
        [attr.aria-label]="'milestone-select'"
        [attr.data-testid]="'milestone-select'"
        styleClass="w-full"
      >
        <ng-template let-milestone pTemplate="item">
          <div class="flex flex-column">
            <span class="font-medium">{{ milestone.name || ('CLIENT_MILESTONE.UNNAMED' | translate) }}</span>
            <small class="text-600">{{ milestone.tierName }} - {{ milestone.targetValue }} {{ milestone.unit }}</small>
          </div>
        </ng-template>
      </p-select>
    </div>

    <!-- Achieved Value -->
    <div class="field mb-4">
      <label for="achieved-value" class="block text-900 font-medium mb-2">
        {{ 'CLIENT_MILESTONE.OVERRIDE_AWARD.ACHIEVED_VALUE' | translate }}
      </label>
      <p-inputnumber
        id="achieved-value"
        formControlName="achievedValue"
        [min]="0"
        [useGrouping]="false"
        [attr.aria-label]="'achieved-value-input'"
        [attr.data-testid]="'achieved-value-input'"
        styleClass="w-full"
      />
    </div>

    <!-- Achieved Date -->
    <div class="field mb-4">
      <label for="achieved-date" class="block text-900 font-medium mb-2">
        {{ 'CLIENT_MILESTONE.OVERRIDE_AWARD.ACHIEVED_DATE' | translate }}
      </label>
      <p-datepicker
        id="achieved-date"
        formControlName="achievedDate"
        [showIcon]="true"
        [appendTo]="'body'"
        dateFormat="yy-mm-dd"
        [attr.aria-label]="'achieved-date-picker'"
        [attr.data-testid]="'achieved-date-picker'"
        styleClass="w-full"
      />
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-content-end gap-2">
      <button
        pButton
        pRipple
        type="button"
        [label]="'common.BUTTONS.CANCEL-BUTTON-TEXT' | translate"
        class="p-button-text"
        (click)="closeOverrideDialog()"
        [disabled]="awardLoading()"
        [attr.aria-label]="'cancel-award-button'"
        [attr.data-testid]="'cancel-award-button'"
      ></button>
      <button
        pButton
        pRipple
        type="button"
        [label]="'CLIENT_MILESTONE.OVERRIDE_AWARD.SUBMIT' | translate"
        (click)="submitAwardMilestone()"
        [loading]="awardLoading()"
        [disabled]="awardForm.invalid"
        [attr.aria-label]="'submit-award-button'"
        [attr.data-testid]="'submit-award-button'"
      ></button>
    </div>
  </ng-template>
</p-dialog>

<!-- Revoke Milestone Dialog -->
<p-dialog
  [visible]="showRevokeDialog()"
  (onHide)="closeRevokeDialog()"
  [modal]="true"
  [resizable]="false"
  [draggable]="false"
  [dismissableMask]="false"
  [closeOnEscape]="false"
  appendTo="body"
  [style]="{ width: '500px' }"
  [breakpoints]="{ '767px': '95vw' }"
  [attr.aria-label]="'revoke-milestone-dialog'"
  [attr.data-testid]="'revoke-milestone-dialog'"
>
  <ng-template pTemplate="header">
    <div class="flex align-items-center gap-2 w-full">
      <i class="pi pi-exclamation-triangle text-red-500 text-xl"></i>
      <h3 class="mb-0 text-base font-bold text-900">
        {{ 'CLIENT_MILESTONE.REVOKE.TITLE' | translate }}
      </h3>
    </div>
  </ng-template>

  <div class="p-fluid">
    <!-- Warning Message -->
    <div class="bg-red-50 border-1 border-red-200 border-round p-3 mb-4">
      <div class="flex align-items-start gap-2">
        <i class="pi pi-info-circle text-red-600 mt-1"></i>
        <p class="text-sm text-red-700 m-0">
          {{ 'CLIENT_MILESTONE.REVOKE.WARNING' | translate }}
        </p>
      </div>
    </div>

    <!-- Milestone Details -->
    @if (selectedMilestoneToRevoke()) {
      <div class="surface-100 border-round p-3 mb-4">
        <div class="text-xs text-500 uppercase mb-2">
          {{ 'CLIENT_MILESTONE.REVOKE.MILESTONE_TO_REVOKE' | translate }}
        </div>
        <div class="font-bold text-900 mb-2">
          {{ selectedMilestoneToRevoke()!.name }}
        </div>
        <div class="flex flex-wrap gap-3 text-sm text-600">
          @if (selectedMilestoneToRevoke()!.achievementId) {
            <div>
              <span class="font-medium">{{ 'CLIENT_MILESTONE.REVOKE.ACHIEVEMENT_ID' | translate }}:</span>
              <span class="ml-1">ach-{{ selectedMilestoneToRevoke()!.achievementId }}</span>
            </div>
          }
          @if (selectedMilestoneToRevoke()!.tierName) {
            <div>
              <span class="font-medium">{{ 'CLIENT_MILESTONE.REVOKE.TIER' | translate }}:</span>
              <span class="ml-1">{{ selectedMilestoneToRevoke()!.tierName }}</span>
            </div>
          }
          @if (selectedMilestoneToRevoke()!.achievedDate) {
            <div>
              <span class="font-medium">{{ 'CLIENT_MILESTONE.REVOKE.ACHIEVED_DATE_LABEL' | translate }}:</span>
              <span class="ml-1">{{ selectedMilestoneToRevoke()!.achievedDate | date: 'd MMM yyyy' }}</span>
            </div>
          }
        </div>
      </div>
    }

    <!-- Reason for Revocation -->
    <div class="field mb-4" [formGroup]="revokeForm">
      <label for="revoke-reason" class="block text-900 font-medium mb-2">
        {{ 'CLIENT_MILESTONE.REVOKE.REASON_LABEL' | translate }}
        <span class="text-red-500">*</span>
      </label>
      <small class="block text-600 mb-2">
        {{ 'CLIENT_MILESTONE.REVOKE.REASON_HELP' | translate }}
      </small>
      <textarea
        pInputTextarea
        id="revoke-reason"
        formControlName="reason"
        rows="4"
        [placeholder]="'CLIENT_MILESTONE.REVOKE.REASON_PLACEHOLDER' | translate"
        class="w-full"
        [attr.aria-label]="'revoke-reason-textarea'"
        [attr.data-testid]="'revoke-reason-textarea'"
      ></textarea>
      @if (revokeForm.get('reason')?.invalid && revokeForm.get('reason')?.touched) {
        <small class="text-red-500 mt-1 block">
          {{ 'CLIENT_MILESTONE.REVOKE.REASON_REQUIRED' | translate }}
        </small>
      }
    </div>

    <!-- System Milestone Note -->
    <div class="surface-50 border-1 border-300 border-round p-3">
      <p class="text-xs text-600 m-0">
        <i class="pi pi-info-circle mr-1"></i>
        {{ 'CLIENT_MILESTONE.REVOKE.SYSTEM_NOTE' | translate }}
      </p>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-content-end gap-2">
      <button
        pButton
        pRipple
        type="button"
        [label]="'common.BUTTONS.CANCEL-BUTTON-TEXT' | translate"
        class="p-button-text"
        (click)="closeRevokeDialog()"
        [disabled]="revokeLoading()"
        [attr.aria-label]="'cancel-revoke-button'"
        [attr.data-testid]="'cancel-revoke-button'"
      ></button>
      <button
        pButton
        pRipple
        type="button"
        icon="pi pi-trash"
        [label]="'CLIENT_MILESTONE.REVOKE.SUBMIT_BUTTON' | translate"
        class="p-button-danger"
        (click)="submitRevokeMilestone()"
        [loading]="revokeLoading()"
        [disabled]="revokeForm.invalid"
        [attr.aria-label]="'submit-revoke-button'"
        [attr.data-testid]="'submit-revoke-button'"
      ></button>
    </div>
  </ng-template>
</p-dialog>

